<span class="sem-translation-pills" *ngIf="languages.length > 0 && !hidden">
  <!--
    The pills flex ROW is its own element, so the absolute-positioned popover below is a sibling of
    the row rather than a flex item inside it. Before this split, browsers were reserving flex-line
    space for the popover's 340px min-width even though position:absolute should have removed it from
    flow, and the pills row jumped rightward the moment a pill was clicked.
  -->
  <span class="sem-translation-pills-row">
    <button
      type="button"
      class="sem-translation-pill"
      *ngFor="let lang of visibleLanguages"
      [class.filled]="hasValue(lang)"
      [class.pending-removal]="isPendingRemoval(lang)"
      [class.is-open]="openLanguage === lang"
      [attr.data-lang]="lang"
      [attr.data-field]="fieldName"
      [title]="tooltip(lang)"
      (click)="toggle(lang)"
    >
      <span class="sem-translation-code">{{ lang.toUpperCase() }}</span>
      <span class="sem-translation-preview" *ngIf="hasValue(lang) || isPendingRemoval(lang)">{{ preview(lang) }}</span>
      <!-- An unsaved deletion has to be visibly different from one the database has accepted. -->
      <span class="material-symbols-outlined sem-translation-undo" *ngIf="isPendingRemoval(lang)">undo</span>
    </button>

    <!-- Reveals the empty language slots for THIS field only, and only for as long as it is open. -->
    <button
      type="button"
      class="sem-translation-globe"
      *ngIf="hasHiddenLanguages || showEmpty"
      [class.on]="showEmpty"
      [title]="showEmpty ? t('galis_js_translation_hide_empty', 'Skrij prazne prevode') : t('galis_js_translation_show_all', 'Pokaži vse jezike')"
      (click)="toggleShowEmpty()"
    ><span class="material-symbols-outlined">language</span></button>
  </span>

  <span
    class="sem-translation-popover"
    *ngIf="openLanguage"
    [class.flip-up]="flipUp"
    [class.multiline]="multiline"
    [attr.data-lang]="openLanguage"
  >
    <span class="sem-translation-popover-head">
      <span class="sem-translation-popover-lang">{{ languageName(openLanguage) }}</span>
      <span class="sem-translation-popover-field">{{ fieldLabel }}</span>
    </span>

    <!--
      The Slovene original. ALWAYS drawn, even when there is nothing to draw: omitting it made "there
      is no source" and "this screen forgot to show it" look identical, and a curator wrote a German
      description of a blank field without ever being told.
    -->
    <span class="sem-translation-source" [class.empty]="sourceEmpty" [title]="sourceValue">
      <span class="sem-translation-source-tag" [title]="t('galis_js_translation_source', 'Izvirnik')">SL</span>
      <span *ngIf="!sourceEmpty">{{ sourceValue }}</span>
      <span class="sem-translation-source-missing" *ngIf="sourceEmpty">{{ t('galis_js_translation_source_empty', 'izvirnik je prazen – prevajate polje, ki v slovenščini še ni izpolnjeno') }}</span>
    </span>

    <span class="sem-translation-row">
      <textarea
        *ngIf="multiline"
        class="sem-translation-input sem-translation-textarea"
        rows="5"
        [attr.data-lang]="openLanguage"
        [attr.data-field]="fieldName"
        [attr.maxlength]="maxLength > 0 ? maxLength : null"
        [disabled]="disabled"
        [value]="draft"
        [placeholder]="placeholder(openLanguage)"
        (input)="onDraftInput($any($event.target).value)"
        (keydown.control.enter)="commit()"
        (keydown.meta.enter)="commit()"
      ></textarea>

      <input
        *ngIf="!multiline"
        type="text"
        class="sem-translation-input"
        [attr.data-lang]="openLanguage"
        [attr.data-field]="fieldName"
        [attr.maxlength]="maxLength > 0 ? maxLength : null"
        [disabled]="disabled"
        [value]="draft"
        [placeholder]="placeholder(openLanguage)"
        (input)="onDraftInput($any($event.target).value)"
        (keydown.enter)="commit()"
      />
    </span>

    <!--
      Counter and key hints. The counter is the same idiom and the same limit as the field being
      translated (read off that field, not hardcoded); the hints exist because every complaint in the
      curator pass was about not being told the rules rather than about the mechanism.
    -->
    <span class="sem-translation-meta">
      <span class="sem-translation-hint">{{ (multiline ? t('galis_js_translation_hint_multiline', 'Ctrl+Enter potrdi') : t('galis_js_translation_hint', 'Enter potrdi')) }} · {{ t('galis_js_translation_hint_esc', 'Esc prekliče') }}</span>
      <span class="sem-translation-counter" *ngIf="maxLength > 0" [class.at-limit]="draft.length >= maxLength">{{ draft.length }}/{{ maxLength }}</span>
    </span>

    <!--
      Actions. Two families on one row, deliberately not one list of five.

      LEFT — what to do WITH the translation. Labelled text buttons. Remove is labelled rather than
      an icon because the × that used to sit here carried both meanings at once ("close this" and
      "the only button on offer") and read as a confirm.

      RIGHT — how to LEAVE the popover: the commit/cancel pair, icon-only and pushed to the corner so
      it reads as a separate thing. An earlier note here argued a popover should answer to Esc and
      click-away and carry no confirm/cancel buttons at all. That was wrong for the actual job: a
      curator filling four languages in a row works entirely with the mouse, and keyboard-only exits
      forced a reach back to Esc between every one. Esc and Enter still do exactly what they did —
      this is additive, and the hint below still names them.
    -->
    <span class="sem-translation-actions">
      <button
        type="button"
        class="sem-translation-action suggest"
        *ngIf="suggestAvailable && !disabled && sourceValue"
        [disabled]="suggesting"
        (click)="suggest(openLanguage)"
      >
        <span class="material-symbols-outlined">auto_awesome</span>
        {{ suggesting ? t('galis_js_translation_suggesting', 'Prevajam …') : t('galis_js_translation_suggest', 'Predlagaj prevod') }}
      </button>

      <button
        type="button"
        class="sem-translation-action remove"
        *ngIf="canRemove(openLanguage)"
        (click)="removeTranslation(openLanguage)"
      >
        <span class="material-symbols-outlined">delete</span>
        {{ t('galis_js_translation_remove', 'Odstrani prevod') }}
      </button>

      <span class="sem-translation-commit-pair">
        <!--
          Same handlers as Enter and Esc, called directly — no second copy of the commit rule.
          Never disabled: commit() is not gated either, and an unchanged draft simply closes. A check
          that goes dead the moment there is nothing to save would strand a mouse user who opened a
          pill and changed their mind, which is the exact reach-for-the-keyboard this pair removes.
          maxLength is enforced by the native maxlength attribute, so an over-long draft cannot exist.
        -->
        <button
          type="button"
          class="sem-translation-icon confirm"
          *ngIf="!disabled"
          [attr.aria-label]="t('galis_js_translation_confirm', 'Potrdi prevod')"
          [title]="t('galis_js_translation_confirm', 'Potrdi prevod')"
          (click)="commit()"
        ><span class="material-symbols-outlined">check</span></button>

        <!-- On a read-only field there is nothing to confirm, so the cross is a plain close. -->
        <button
          type="button"
          class="sem-translation-icon cancel"
          [attr.aria-label]="disabled ? t('galis_js_translation_close', 'Zapri (Esc)') : t('galis_js_translation_cancel', 'Prekliči urejanje')"
          [title]="disabled ? t('galis_js_translation_close', 'Zapri (Esc)') : t('galis_js_translation_cancel', 'Prekliči urejanje')"
          (click)="cancel()"
        ><span class="material-symbols-outlined">close</span></button>
      </span>
    </span>
  </span>
</span>
